import React, { FC, useContext } from 'react';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';

import {
    isTravelTimeSearchSelector,
    filtersValuesSelector,
    locationSelector,
    identifierSelector,
    locationTextSelector
} from '../../../reducers/filters';

import { selectCurrentSortOrder } from '../../../reducers/listingsSortOrder';
import {
    BedroomsWrapper,
    customThemeOverrides,
    SearchInitationWrapper,
    PriceRangeMultiColumnDropdownWrapper,
    PropertyTypeWrapper,
    SearchRadiusWrapper,
    StyledButtonWrapper,
    StyledButton,
    TravelTimeWrapper,
    StyledDivider,
    StyledDividerInner,
    LocationWrapper,
    HousePricesLocationWrapper
} from './SearchInitiation.styles';
import { SelectPriceForSale } from './SelectPriceForSale';
import { SelectPriceToRent } from './SelectPriceToRent';
import { SelectRadius } from './SelectRadius';
import { SelectBeds } from './SelectBeds';
import { SelectPropertyType } from './SelectPropertyType';
import { SelectMaxTravelTimeAndTransportMode } from './SelectMaxTravelTimeAndTransportMode';
import { SearchContext, SearchPageType } from '../../../search/SearchContext';
import { LocationSearchInput } from '../LocationSearchInput/LocationSearchInput';
import { getTranslator } from '../../i18n/getTranslator';
import searchTranslations from 'public/locales/en/search.json';
import { Tab } from '../../../homepage/components/HeroContainer/HeroContainer.types';
import { ThemeProvider } from '@blueprint/ui';
import { searchHousePrices, searchProperties } from 'search/helpers';

const t = getTranslator(searchTranslations);

const SEARCH_BUTTON_TEXT = t('filters.search');

type SearchInitiationProps = {
    searchType: Tab;
    hasRadius?: boolean;
};

export const SearchInitiation: FC<SearchInitiationProps> = ({
    searchType,
    hasRadius = true
}) => {
    const router = useRouter();

    const { state, dispatch } = useContext(SearchContext);
    const filters = useSelector(filtersValuesSelector);

    const { selectedValue: locationValue } = useSelector(locationSelector);
    const { selectedValue: identifier } = useSelector(identifierSelector);
    const isTravelTimeSearch = useSelector(isTravelTimeSearchSelector);
    const { selectedValue: locationTextValue } = useSelector(
        locationTextSelector
    );
    const currentSortOrder = useSelector(selectCurrentSortOrder);

    const { userAlertId } = state;
    const isForSaleSearch = searchType === 'for-sale';
    const isHousePricesSearch = searchType === 'house-prices';

    const handleSearchProperties = () => {
        const searchSource = 'refine';
        searchProperties(
            userAlertId,
            router,
            dispatch,
            searchType as SearchPageType,
            searchSource,
            filters,
            currentSortOrder
        );
    };

    const handleSearchHousePrices = () => {
        const value = locationTextValue || locationValue;

        searchHousePrices(value, identifier);
    };

    const renderRadiusAndTravelTime = () => {
        if (!isTravelTimeSearch) {
            if (hasRadius) {
                return (
                    <>
                        <StyledDivider>
                            <StyledDividerInner />
                        </StyledDivider>
                        <SearchRadiusWrapper>
                            <SelectRadius />
                        </SearchRadiusWrapper>
                    </>
                );
            }

            return null;
        }

        return (
            <ThemeProvider theme={customThemeOverrides}>
                <TravelTimeWrapper>
                    <SelectMaxTravelTimeAndTransportMode />
                </TravelTimeWrapper>
            </ThemeProvider>
        );
    };

    if (isHousePricesSearch) {
        return (
            <ThemeProvider theme={customThemeOverrides}>
                <SearchInitationWrapper>
                    <HousePricesLocationWrapper>
                        <LocationSearchInput
                            id="header-location"
                            onLocationInputKeyDown={handleSearchHousePrices}
                            hasExtendedHeight
                            hasBorderRight={false}
                            searchType="house-prices"
                            value={locationValue}
                        />
                    </HousePricesLocationWrapper>
                    <StyledButtonWrapper>
                        <StyledButton
                            type="button"
                            onClick={handleSearchHousePrices}
                            data-testid="house-prices-search-button"
                            disabled={!(locationValue || locationTextValue)}
                        >
                            {SEARCH_BUTTON_TEXT}
                        </StyledButton>
                    </StyledButtonWrapper>
                </SearchInitationWrapper>
            </ThemeProvider>
        );
    }

    return (
        <ThemeProvider theme={customThemeOverrides}>
            <SearchInitationWrapper>
                <LocationWrapper>
                    <LocationSearchInput
                        id="header-location"
                        onLocationInputKeyDown={handleSearchProperties}
                        hasExtendedHeight
                        hasBorderRight={false}
                        value={locationValue}
                    />
                </LocationWrapper>
                {renderRadiusAndTravelTime()}

                <StyledDivider>
                    <StyledDividerInner />
                </StyledDivider>

                <BedroomsWrapper>
                    <SelectBeds />
                </BedroomsWrapper>

                <StyledDivider>
                    <StyledDividerInner />
                </StyledDivider>

                <PriceRangeMultiColumnDropdownWrapper>
                    {isForSaleSearch ? (
                        <SelectPriceForSale />
                    ) : (
                        <SelectPriceToRent />
                    )}
                </PriceRangeMultiColumnDropdownWrapper>

                <StyledDivider>
                    <StyledDividerInner />
                </StyledDivider>

                <PropertyTypeWrapper>
                    <SelectPropertyType />
                </PropertyTypeWrapper>
                <StyledButtonWrapper>
                    <StyledButton
                        type="button"
                        onClick={handleSearchProperties}
                        data-testid="search-button"
                    >
                        {SEARCH_BUTTON_TEXT}
                    </StyledButton>
                </StyledButtonWrapper>
            </SearchInitationWrapper>
        </ThemeProvider>
    );
};
